// DysphagiaTherapyPlusCM.com - Production Content
// Phase 2A: Content Finalization
// Phase 2B: Email Architecture
// Phase 3A: Insurance UX Enhancements
// Last Updated: December 2024 - Client Content Update

export const CONTENT_VERSION = "3.1.0";

// ============================================================================
// SITE CONFIGURATION
// ============================================================================
export const siteConfig = {
  name: "Dysphagia Therapy Plus CM, LLC",
  tagline: "Specialized Care for Swallowing & Communication",
  phone: "(346) 902-1516",
  phoneLabel: "Office",
  phoneFormatted: "+13469021516",
  fax: "(713) 673-5113",
  faxLabel: "Secure Fax",
  faxFormatted: "+17136735113",
  faxVerified: true,
  // eFax workflow email (internal use only - NOT for public display as fax)
  internalNotificationEmail: "dysphaygiatherapyplus1@outlook.com",
  email: "info@dysphagiatherapypluscm.com",
  address: "Mobile & Community-Based Services",
  city: "Houston",
  state: "TX",
  zip: "",
  serviceArea: "Greater Houston and Surrounding Communities",
  hours: "Monday – Friday: 9:00 AM – 5:00 PM",
  hoursNote: "By appointment only",
  mobileServices: true,
  mobileNote: "Mobile in-home services available throughout the Greater Houston area"
};

// ============================================================================
// SIMPLEPRACTICE INTEGRATION - PAUSED (Coming Soon)
// ============================================================================
// Status: PAUSED - Client requested delay until activation approved
// Plan: SimplePractice Plus
// Integration Method: Client Portal URL / External Website Widget
export const simplePractice = {
  enabled: false,
  status: "COMING_SOON",
  clientPortalUrl: null,
  widgetCode: null,
  bookingLabel: "Book an Evaluation",
  portalLabel: "Patient Portal",
  portalStatus: "Coming Soon",
  placeholderMessage: "Patient Portal coming soon"
};

// ============================================================================
// PROVIDER PORTAL - PAUSED (Coming Soon)
// ============================================================================
// Status: PAUSED - Client requested delay until activation approved
// Providers can still view public referral information page
export const providerPortal = {
  enabled: false,
  status: "COMING_SOON",
  portalLabel: "Provider Portal",
  portalStatus: "Coming Soon",
  placeholderMessage: "Provider Portal coming soon"
};

// ============================================================================
// GOOGLE REVIEWS CONFIGURATION - VERIFIED PROFILE
// ============================================================================
// Verified Business Profile: 5445 Almeda Rd, Suite 450, Houston, TX 77004
// Do NOT use duplicate/unverified profiles
export const googleReviews = {
  enabled: true,
  reviewsUrl: "https://www.google.com/search?q=Dysphagia+Therapy+Plus+LLC&stick=H4sIAAAAAAAA_-NgU1I1qLAwMzFISjMwNDMzTEw0TTS2MqgwSTY3Nk9KM0kzTjMxNTc1WcQq5VJZXJCRmJ6ZqBCSkVqUWFCpEJBTWqzg4-MMAKgs55pGAAAA&hl=en&mat=CSDUS7qlRRxQElcBzAmVZtjzNnMspBEnCtW3_wsUXr8aX9EzZZQESQ92UVqEGRSJ7u5HCskc_cR82va3guzaveItOkQ0z7v2iHepXeRUyG-HuKPczjoGvQ6YYOVullVbjkU&authuser=0&ved=1t:350944",
  profileUrl: "https://www.google.com/search?q=Dysphagia+Therapy+Plus+LLC",
  businessAddress: "5445 Almeda Rd, Suite 450, Houston, TX 77004",
  heading: "Patient Experiences",
  subheading: "See what patients and families are saying about their experience with Dysphagia Therapy Plus.",
  ctaRead: "Read Our Google Reviews",
  ctaLeave: "Leave Us a Google Review",
  ctaAfterSubmit: "Had a great experience? Share it on Google!"
};

// ============================================================================
// VERIFIED TRUST INDICATORS (No unsupported percentages)
// ============================================================================
export const verifiedStats = {
  yearsExperience: 28,
  patientsServed: 500,
  certification: "CCC-SLP",
  certificationLabel: "ASHA Certification",
  insuranceAccepted: true
};

// ============================================================================
// INSURANCE INFORMATION
// ============================================================================
export const insuranceInfo = {
  acceptsInsurance: true,
  headline: "Insurance Accepted",
  shortDescription: "Insurance is accepted for eligible plans. Coverage and benefits vary by carrier and plan.",
  callToAction: "Contact us to verify your benefits before your first visit.",
  verificationNote: "Insurance verification available prior to your first visit.",
  selfPayAvailable: true,
  selfPayNote: "Self-pay options available for those without insurance coverage.",
};

export const referralSources = [
  { value: "physician", label: "Physician Referral" },
  { value: "hospital", label: "Hospital / Rehab Facility" },
  { value: "insurance", label: "Insurance Provider Directory" },
  { value: "online", label: "Online Search (Google, etc.)" },
  { value: "family", label: "Family or Friend" },
  { value: "other", label: "Other" },
];

export const paymentTypes = [
  { value: "insurance", label: "I plan to use insurance" },
  { value: "self-pay", label: "Self-pay" },
  { value: "unsure", label: "Not sure yet" },
];

export const trustBadges = [
  { label: "Licensed & ASHA-Certified", icon: "Award" },
  { label: "Insurance Accepted", icon: "Shield" },
  { label: "Evidence-Based Care", icon: "CheckCircle" },
  { label: "HIPAA-Conscious Intake", icon: "Lock" },
];

export const insuranceFaqs = [
  {
    question: "Do you accept my insurance?",
    answer: "Insurance is accepted for eligible plans. Coverage varies by carrier and plan. Please contact us with your insurance information and we'll verify your benefits before your first visit."
  },
  {
    question: "What information do I need to verify my insurance?",
    answer: "Have your insurance card ready when you call. We'll need your insurance company name, member ID, and group number. We handle the verification process for you."
  },
  {
    question: "Do I need a referral from my doctor?",
    answer: "Some insurance plans require a physician referral for speech-language pathology services. We can help you determine if your plan requires one and guide you through the process."
  },
  {
    question: "What will I pay out of pocket?",
    answer: "Your out-of-pocket costs depend on your specific plan, including your deductible, copay, and coinsurance. We'll explain your estimated costs after verifying your benefits."
  },
  {
    question: "What if I don't have insurance?",
    answer: "We offer self-pay options for patients without insurance coverage. Contact us to discuss payment arrangements and get information about our self-pay rates."
  },
  {
    question: "What should I bring to my first appointment?",
    answer: "Please bring your insurance card, a valid photo ID, any physician referral or prescription (if applicable), and a list of current medications. Arriving 10-15 minutes early helps ensure a smooth check-in."
  },
];

export const firstVisitChecklist = [
  "Insurance card (front and back)",
  "Valid photo ID (driver's license or state ID)",
  "Physician referral or prescription (if required by your plan)",
  "List of current medications",
  "Any relevant medical records or test results",
  "List of questions or concerns you'd like to discuss",
];

// ============================================================================
// CLINICIAN PROFILE
// ============================================================================
export const clinician = {
  name: "Gilda Jack-Johnson",
  credentials: "M.Ed., CCC-SLP",
  fullCredentials: "Master of Education (M.Ed.), Certificate of Clinical Competence in Speech-Language Pathology (CCC-SLP)",
  title: "Licensed Speech-Language Pathologist",
  subtitle: "ASHA-certified Speech-Language Pathologist",
  photo: "/images/gilda-jack-johnson.png", // Approved clinician photograph
  experience: "28 Years",
  experienceYears: 28,
  patientsServed: 500,
  specialties: [
    "Dysphagia (Swallowing Disorders)",
    "Speech & Language Disorders",
    "Cognitive-Communication Therapy",
    "Voice Therapy",
    "Caregiver Education & Training"
  ],
  certifications: [
    "Certificate of Clinical Competence in Speech-Language Pathology (CCC-SLP)",
    "American Speech-Language-Hearing Association (ASHA) Certified",
    "Licensed Speech-Language Pathologist - Texas"
  ],
  bio: `Gilda specializes in helping adults with swallowing, speech, voice, language, and cognitive-communication challenges through compassionate, evidence-based care.`,
  fullBio: `Gilda Jack-Johnson, M.Ed., CCC-SLP is a licensed and ASHA-certified Speech-Language Pathologist with 28 years of clinical experience in the evaluation and treatment of swallowing disorders (dysphagia), as well as speech, voice, and cognitive-communication impairments. She is dedicated to helping patients regain safe swallowing function, improve communication abilities, and enhance overall quality of life through evidence-based therapy.

With a compassionate and patient-centered approach, Gilda works closely with individuals, caregivers, and interdisciplinary healthcare teams to develop personalized treatment plans tailored to each patient's specific needs. Her practice emphasizes safety, dignity, functional outcomes, and education to empower patients and families throughout the therapy process.

Gilda is committed to delivering high-quality care across a variety of settings and takes pride in building trusting relationships that support long-term progress and confidence.`
};

// ============================================================================
// SERVICES
// ============================================================================
export const services = [
  {
    id: "comprehensive-evaluation",
    title: "Comprehensive Swallowing Evaluation",
    shortDescription: "Thorough assessment to identify swallowing difficulties and develop personalized treatment plans for safer eating.",
    icon: "ClipboardCheck",
    whoIsItFor: "Adults experiencing difficulty swallowing, frequent coughing during meals, unexplained weight loss, or concerns about eating safely.",
    whatToExpect: "A detailed clinical evaluation including medical history review, oral motor examination, and trial swallows with various food and liquid textures to assess your swallowing function.",
    outcomes: "Clear diagnosis, personalized treatment recommendations, and a roadmap for improved swallowing safety and quality of life.",
    duration: "60-90 minutes"
  },
  {
    id: "dysphagia-therapy",
    title: "Dysphagia Therapy",
    shortDescription: "Individualized treatment programs to improve swallowing safety, strength, and efficiency.",
    icon: "HeartPulse",
    whoIsItFor: "Patients diagnosed with dysphagia who want to improve their swallowing function and eat more safely.",
    whatToExpect: "Customized exercises to strengthen swallowing muscles, compensatory strategies, diet texture modifications, and ongoing progress monitoring tailored to your goals.",
    outcomes: "Improved swallowing safety, reduced aspiration risk, better nutrition, and enhanced quality of life.",
    duration: "45-60 minutes per session"
  },
  {
    id: "speech-language-therapy",
    title: "Speech & Language Therapy",
    shortDescription: "Targeted therapy to improve speech clarity, language skills, and communication abilities.",
    icon: "MessageCircle",
    whoIsItFor: "Adults with speech difficulties, language impairments, or communication challenges following stroke, brain injury, or other conditions.",
    whatToExpect: "Personalized exercises and techniques to improve articulation, word-finding, sentence formation, reading comprehension, and overall communication effectiveness.",
    outcomes: "Clearer speech, improved language skills, greater confidence in communication, and enhanced independence.",
    duration: "45-60 minutes per session"
  },
  {
    id: "cognitive-communication",
    title: "Cognitive-Communication Therapy",
    shortDescription: "Specialized treatment for thinking and communication skills affected by neurological conditions.",
    icon: "Brain",
    whoIsItFor: "Patients experiencing memory difficulties, problem-solving challenges, or communication changes due to stroke, brain injury, or progressive conditions.",
    whatToExpect: "Evidence-based exercises targeting memory recall, attention, reasoning, and functional problem-solving skills, with strategies you can use in daily life.",
    outcomes: "Improved memory strategies, better functional problem-solving, and greater independence in daily activities.",
    duration: "45-60 minutes per session"
  },
  {
    id: "voice-therapy",
    title: "Voice Therapy",
    shortDescription: "Treatment to restore and improve voice quality, strength, and endurance.",
    icon: "Mic",
    whoIsItFor: "Individuals with voice disorders, hoarseness, vocal fatigue, or voice changes following medical treatment or surgery.",
    whatToExpect: "Targeted vocal exercises, breathing techniques, and voice hygiene education to improve vocal quality and reduce strain.",
    outcomes: "Stronger, clearer voice with improved endurance and reduced vocal fatigue.",
    duration: "45-60 minutes per session"
  },
  {
    id: "caregiver-training",
    title: "Caregiver Education & Training",
    shortDescription: "Empowering families and caregivers with knowledge and skills to support safe eating and communication.",
    icon: "Users",
    whoIsItFor: "Family members and caregivers of individuals with swallowing difficulties or communication challenges.",
    whatToExpect: "Hands-on training in safe feeding techniques, diet preparation, recognizing warning signs, and communication strategies to support your loved one.",
    outcomes: "Confident caregivers equipped to provide safe, supportive care and improve their loved one's quality of life.",
    duration: "30-45 minutes"
  }
];

// ============================================================================
// TESTIMONIALS (Real - from dysphagiatherapypluscm.com/testimonials)
// ============================================================================
export const testimonials = [
  {
    id: 1,
    name: "Marcella C.",
    condition: "Post-Stroke Rehabilitation",
    quote: "Being a retired teacher with over 30 years of experience and after losing my ability to write, read, as well as being able to perform basic math skills, I was devastated. Gilda worked with me for many months, reteaching me my letters and numbers. She helped me learn how to read again. She also taught me how to manage numbers. I can even write checks again. Gilda was always punctual and dependable. She was always positive and very encouraging. She even taught me many things I didn't even know I'd lost, like swallowing safely. Gilda always said I was her blessing. But the truth is, she was my blessing!",
    outcome: "Regained reading, writing, and math skills"
  },
  {
    id: 2,
    name: "Joan C.D.",
    condition: "Rehabilitation Center Care",
    quote: "I met Mrs. Johnson while in a rehab center in Clear Lake, Texas. She walked into our center and made me feel so at home and was such an inspirational person. I am glad she came into my life. She was honest, knowledgeable, and compassionate. She is always in contact with me. I would say not everyone that you meet in life is interested in the client's wellbeing. Mrs. Johnson is very concerned about her clients which is revealed by her quality of professional services.",
    outcome: "Ongoing supportive care since 2020"
  },
  {
    id: 3,
    name: "Joan C.D.",
    condition: "Speech & Swallowing Therapy",
    quote: "Mrs. Johnson's Speech Therapy Services were helpful and very efficient. She is knowledgeable about speech therapy and swallowing skills. Her services helped with my short term memory recall, higher level reasoning, reading comprehension as well as graphics skills. Mrs. Johnson provided education and training on appropriate meal consumption addressing adequate portion sizes of food and liquids. Her Speech and Dysphagia Therapy Services assisted me to return home and function independently. I am also able to return to driving short distances to run errands.",
    outcome: "Returned home and regained independence"
  },
  {
    id: 4,
    name: "Edward M.",
    condition: "Throat Cancer Recovery",
    quote: "Mrs. Johnson has been assisting with enhancement of my swallowing, short term memory recall and functional problem solving skills. Her services have helped me to be in better control of and understand my swallowing muscles after experiencing throat cancer. Gilda has provided guidance with nutritional education and training which has been beneficial to my medical situation. As a result, I gained twenty pounds over the last three months, while focusing on adequate nutrition and hydration.",
    outcome: "Gained 20 pounds, improved swallowing control"
  },
  {
    id: 5,
    name: "Edward M.",
    condition: "Memory & Daily Living Skills",
    quote: "Mrs. Johnson helped me to understand the significance of having three balanced meals, daily snacks and supplemental nutrition drinks daily. As my consumption of food and liquids increased, my short term memory skills improved. I used the memory techniques which she demonstrated during speech therapy treatments. My family and I noticed my ability to use the memory strategies in my daily living activities.",
    outcome: "Improved memory and daily function since 2019"
  }
];

// ============================================================================
// FREQUENTLY ASKED QUESTIONS
// ============================================================================
export const faqs = [
  {
    question: "What is dysphagia?",
    answer: "Dysphagia is the medical term for difficulty swallowing. It can affect any part of the swallowing process, from moving food through the mouth to getting it safely into the stomach. Dysphagia can lead to malnutrition, dehydration, and aspiration pneumonia if not properly managed."
  },
  {
    question: "What are the signs I might have a swallowing problem?",
    answer: "Common signs include coughing or choking during or after eating, feeling like food is stuck in your throat, unexplained weight loss, recurring pneumonia, wet or gurgly voice after eating, and avoiding certain foods due to difficulty swallowing."
  },
  {
    question: "What causes swallowing disorders?",
    answer: "Swallowing disorders can be caused by neurological conditions (stroke, Parkinson's, MS), head and neck cancer and its treatments, aging, GERD, and structural abnormalities. A thorough evaluation can help identify the underlying cause."
  },
  {
    question: "What happens during a swallowing evaluation?",
    answer: "During a clinical swallowing evaluation, your therapist will review your medical history, examine the muscles used for swallowing, and observe you swallowing different food and liquid textures. Additional instrumental tests may be recommended for a complete picture."
  },
  {
    question: "How long does dysphagia therapy take?",
    answer: "The duration of therapy varies based on the underlying cause and severity of the swallowing disorder. Some patients see improvement in a few weeks, while others may need ongoing therapy. Your therapist will provide a personalized treatment timeline."
  },
  {
    question: "Is dysphagia therapy covered by insurance?",
    answer: "Many insurance plans, including Medicare, cover speech-language pathology services for dysphagia. We recommend contacting your insurance provider to verify your specific coverage and benefits."
  },
  {
    question: "Do you offer virtual appointments?",
    answer: "Initial evaluations are performed in person to ensure a comprehensive assessment. Virtual follow-up appointments may be available for established patients when the clinician determines they are clinically appropriate."
  },
  {
    question: "What areas do you serve?",
    answer: "We provide mobile in-home services throughout Greater Houston and surrounding communities, including Harris, Fort Bend, and Montgomery counties. Contact us to discuss service availability in your area."
  }
];

// ============================================================================
// SYMPTOMS CHECKLIST
// ============================================================================
export const symptoms = [
  "Coughing or choking while eating or drinking",
  "Food or liquid going down the wrong way",
  "Feeling like food is stuck in your throat or chest",
  "Pain when swallowing",
  "Drooling or difficulty managing saliva",
  "Wet or gurgly voice during or after eating",
  "Recurrent pneumonia or chest infections",
  "Unexplained weight loss",
  "Avoiding certain foods or textures",
  "Taking longer than usual to finish meals"
];

// ============================================================================
// SAFETY TIPS
// ============================================================================
export const safetyTips = [
  {
    title: "Sit Upright",
    description: "Always sit at 90 degrees when eating and remain upright for 30 minutes after meals."
  },
  {
    title: "Take Small Bites",
    description: "Use smaller utensils and take small, manageable bites to reduce choking risk."
  },
  {
    title: "Eat Slowly",
    description: "Take your time during meals. Rushing increases the risk of swallowing difficulties."
  },
  {
    title: "Minimize Distractions",
    description: "Turn off the TV and focus on eating to improve swallowing coordination."
  },
  {
    title: "Follow Diet Recommendations",
    description: "Stick to the food textures and liquid consistencies recommended by your therapist."
  },
  {
    title: "Stay Hydrated",
    description: "Drink appropriate fluids throughout the day to maintain hydration and oral health."
  }
];

// ============================================================================
// MISSION & VALUES
// ============================================================================
export const missionValues = {
  mission: "To provide exceptional, evidence-based therapy that helps individuals regain safe swallowing function, improve communication, and enhance quality of life through compassionate, patient-centered care.",
  values: [
    {
      title: "Humility",
      description: "We approach every patient with respect and openness, recognizing that each person's journey is unique."
    },
    {
      title: "Integrity",
      description: "We uphold the highest ethical standards in our practice, providing honest and transparent care."
    },
    {
      title: "Dependability",
      description: "You can count on us to be punctual, consistent, and committed to your progress."
    },
    {
      title: "Quality Care",
      description: "We deliver evidence-based treatment with a focus on safety, dignity, and functional outcomes."
    }
  ],
  corePromise: "You will be treated with Humility, Respect, and Empathy. You can rely on our Dependability. We will work with Integrity. Your visits will be timely and efficient. Our Quality of Care will be above all others."
};
